import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { ArrowUpRight, FlaskConical, Loader2, Play, Plus, Trash2 } from "lucide-react";
import { api, formatApiError } from "../../../lib/api";

const inputCls = "w-full bg-white border border-navy/10 rounded-lg px-3 py-2 text-sm outline-none focus:border-violet transition-colors";

export default function ScenariosTab({ plan, onChange }) {
  const [scenarios, setScenarios] = useState(null);
  const [open, setOpen] = useState(null); // escenario en edición
  const [running, setRunning] = useState("");
  const [compare, setCompare] = useState(null); // {sid, result, baseline, diff}

  const load = useCallback(() => {
    api.get(`/planning/plans/${plan.id}/scenarios`).then((r) => setScenarios(r.data)).catch(() => setScenarios([]));
  }, [plan.id]);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    const nombre = window.prompt("Nombre del escenario:", `Escenario ${(scenarios?.length || 0) + 1}`);
    if (!nombre) return;
    try {
      const { data } = await api.post(`/planning/plans/${plan.id}/scenarios`, { nombre });
      load(); setOpen(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Error"); }
  };

  const save = async () => {
    try {
      const { data } = await api.patch(`/planning/scenarios/${open.id}`, open);
      toast.success("Escenario guardado"); setOpen(data); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Error"); }
  };

  const run = async (sc) => {
    setRunning(sc.id);
    try {
      const { data } = await api.post(`/planning/scenarios/${sc.id}/run`);
      setCompare({ sid: sc.id, nombre: sc.nombre, ...data });
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Error al simular"); }
    finally { setRunning(""); }
  };

  const promote = async (sc) => {
    if (!window.confirm(`¿Promover "${sc.nombre}" al plan? Se aplicarán sus recursos/reglas/fechas y se generará una nueva versión del cronograma.`)) return;
    setRunning(sc.id);
    try {
      const { data } = await api.post(`/planning/scenarios/${sc.id}/promote`);
      toast.success(`Escenario promovido — nueva versión v${data.run.version}`);
      load(); onChange();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Error al promover"); }
    finally { setRunning(""); }
  };

  const del = async (sc) => {
    if (!window.confirm(`¿Eliminar el escenario "${sc.nombre}"?`)) return;
    try { await api.delete(`/planning/scenarios/${sc.id}`); if (open?.id === sc.id) setOpen(null); load(); }
    catch { toast.error("Error"); }
  };

  const updRt = (i, count) => {
    const rts = [...open.resource_types];
    rts[i] = { ...rts[i], count: Number(count) };
    setOpen({ ...open, resource_types: rts });
  };
  const updProd = (i, capacity) => {
    const prs = [...open.productivity_rules];
    prs[i] = { ...prs[i], capacity: Number(capacity) };
    setOpen({ ...open, productivity_rules: prs });
  };
  const toggleRule = (rid) => {
    const dis = new Set(open.disabled_rule_ids || []);
    dis.has(rid) ? dis.delete(rid) : dis.add(rid);
    setOpen({ ...open, disabled_rule_ids: [...dis] });
  };

  if (scenarios === null) return <div className="py-16 text-center"><Loader2 className="w-5 h-5 animate-spin mx-auto text-navy/30" /></div>;

  return (
    <div className="space-y-4" data-testid="scenarios-tab">
      <div className="flex items-center gap-3">
        <p className="text-sm text-navy/50">Duplica las condiciones del plan, cambia recursos, productividades, fechas o reglas y compara el resultado sin tocar el plan activo.</p>
        <button onClick={create} data-testid="new-scenario-btn"
          className="ml-auto rounded-full bg-gradient-violet text-white text-sm px-5 py-2.5 hover:opacity-90 inline-flex items-center gap-2 shrink-0">
          <Plus className="w-4 h-4" /> Nuevo escenario
        </button>
      </div>

      {scenarios.length === 0 && (
        <div className="rounded-xl border border-dashed border-navy/15 bg-white p-12 text-center" data-testid="scenarios-empty">
          <FlaskConical className="w-8 h-8 mx-auto text-navy/20 mb-2" />
          <p className="text-sm text-navy/40">Sin escenarios. Crea uno para simular, por ejemplo, una cuadrilla extra de tendido.</p>
        </div>
      )}

      <div className="grid lg:grid-cols-2 gap-4">
        <div className="space-y-3">
          {scenarios.map((sc) => (
            <div key={sc.id} className={`rounded-xl border bg-white p-4 space-y-2 ${open?.id === sc.id ? "border-violet" : "border-navy/10"}`} data-testid={`scenario-${sc.id}`}>
              <div className="flex items-center gap-2 flex-wrap">
                <button onClick={() => setOpen(open?.id === sc.id ? null : { disabled_rule_ids: [], ...sc })} className="font-medium text-sm hover:text-violet" data-testid={`scenario-open-${sc.id}`}>
                  {sc.nombre}
                </button>
                {sc.promoted && <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">Promovido</span>}
                <div className="ml-auto flex gap-1.5">
                  <button onClick={() => run(sc)} disabled={running === sc.id} title="Ejecutar simulación" data-testid={`scenario-run-${sc.id}`}
                    className="rounded-full border border-navy/15 text-xs px-3 py-1.5 hover:border-violet inline-flex items-center gap-1 disabled:opacity-50">
                    {running === sc.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Play className="w-3.5 h-3.5" />} Simular
                  </button>
                  <button onClick={() => promote(sc)} disabled={running === sc.id} title="Promover al plan" data-testid={`scenario-promote-${sc.id}`}
                    className="rounded-full bg-navy text-cream text-xs px-3 py-1.5 hover:bg-navy-soft inline-flex items-center gap-1 disabled:opacity-50">
                    <ArrowUpRight className="w-3.5 h-3.5" /> Promover
                  </button>
                  <button onClick={() => del(sc)} className="text-navy/25 hover:text-destructive"><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
              {sc.result?.stats && (
                <div className="text-xs text-navy/50 flex flex-wrap gap-x-4 gap-y-0.5">
                  <span>{sc.result.stats.dias_equivalentes} días eq.</span>
                  <span>Fin {sc.result.stats.fecha_fin_proyectada}</span>
                  <span className={sc.result.stats.en_tiempo ? "text-emerald-600" : "text-red-600"}>
                    {sc.result.stats.en_tiempo ? "✓ En tiempo" : `⚠ ${Math.abs(sc.result.stats.variacion_dias)}d fuera`}</span>
                </div>
              )}
              {open?.id === sc.id && (
                <div className="border-t border-navy/10 pt-3 space-y-3" data-testid="scenario-editor">
                  <div className="grid grid-cols-3 gap-2">
                    <label className="text-[10px] text-navy/40">Inicio
                      <input className={inputCls} type="date" value={open.fecha_inicio} onChange={(e) => setOpen({ ...open, fecha_inicio: e.target.value })} /></label>
                    <label className="text-[10px] text-navy/40">Cierre
                      <input className={inputCls} type="date" value={open.fecha_cierre} onChange={(e) => setOpen({ ...open, fecha_cierre: e.target.value })} /></label>
                    <label className="text-[10px] text-navy/40">Buffer (días)
                      <input className={inputCls} type="number" min={0} value={open.buffer_days} data-testid="scenario-buffer"
                        onChange={(e) => setOpen({ ...open, buffer_days: Number(e.target.value) })} /></label>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-navy/40 mb-1.5">Recursos</div>
                    <div className="grid grid-cols-2 gap-2">
                      {open.resource_types.map((rt, i) => (
                        <label key={`${rt.code}-${i}`} className="text-xs text-navy/60 flex items-center gap-2">
                          <input className={`${inputCls} !w-16 !py-1.5`} type="number" min={0} value={rt.count} data-testid={`scenario-rt-${rt.code}`}
                            onChange={(e) => updRt(i, e.target.value)} /> {rt.name}
                        </label>
                      ))}
                    </div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-navy/40 mb-1.5">Productividades (capacidad/día)</div>
                    <div className="grid grid-cols-2 gap-2">
                      {open.productivity_rules.map((pr, i) => (
                        <label key={i} className="text-xs text-navy/60 flex items-center gap-2">
                          <input className={`${inputCls} !w-16 !py-1.5`} type="number" step="0.1" value={pr.capacity}
                            onChange={(e) => updProd(i, e.target.value)} />
                          <span className="truncate">{pr.activity}{pr.value ? ` (${pr.value})` : ""}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                  {(plan.rules || []).length > 0 && (
                    <div>
                      <div className="text-[10px] uppercase tracking-wider text-navy/40 mb-1.5">Reglas activas en este escenario</div>
                      {(plan.rules || []).map((r) => (
                        <label key={r.id} className="text-xs text-navy/60 flex items-start gap-2 py-0.5">
                          <input type="checkbox" checked={!(open.disabled_rule_ids || []).includes(r.id)} onChange={() => toggleRule(r.id)} className="mt-0.5" />
                          <span>{r.resumen || r.original}</span>
                        </label>
                      ))}
                    </div>
                  )}
                  <button onClick={save} data-testid="scenario-save"
                    className="rounded-full bg-navy text-cream text-xs px-4 py-2 hover:bg-navy-soft">Guardar cambios</button>
                </div>
              )}
            </div>
          ))}
        </div>

        <div>
          {compare ? (
            <div className="rounded-xl border border-navy/10 bg-white p-5 space-y-3 sticky top-4" data-testid="scenario-compare">
              <div className="overline text-navy/40">Comparación — {compare.nombre} vs Plan v{compare.baseline_version}</div>
              <table className="w-full text-sm">
                <thead><tr className="text-xs text-navy/40 text-left border-b border-navy/10">
                  <th className="py-2 font-medium">KPI</th><th className="py-2 font-medium">Plan actual</th><th className="py-2 font-medium">Escenario</th></tr></thead>
                <tbody>
                  {[["Días equivalentes", "dias_equivalentes"], ["Fin proyectado", "fecha_fin_proyectada"],
                    ["Días hábiles", "dias_habiles_disponibles"], ["Buffer", "buffer_dias"],
                    ["Variación (días)", "variacion_dias"], ["Cuello de botella", "cuello_botella"]].map(([l, k]) => {
                    const b = compare.baseline?.[k], s = compare.result?.stats?.[k];
                    const better = k === "variacion_dias" && typeof s === "number" && typeof b === "number" ? (s > b ? "text-emerald-600 font-medium" : s < b ? "text-red-600" : "") : "";
                    return (
                      <tr key={k} className="border-b border-navy/5">
                        <td className="py-2 text-navy/50">{l}</td>
                        <td className="py-2">{b ?? "—"}</td>
                        <td className={`py-2 ${better}`}>{s ?? "—"}</td>
                      </tr>
                    );
                  })}
                  <tr>
                    <td className="py-2 text-navy/50">En tiempo</td>
                    <td className="py-2">{compare.baseline?.en_tiempo ? "✓ Sí" : "✗ No"}</td>
                    <td className={`py-2 ${compare.result?.stats?.en_tiempo ? "text-emerald-600 font-medium" : "text-red-600"}`}>{compare.result?.stats?.en_tiempo ? "✓ Sí" : "✗ No"}</td>
                  </tr>
                </tbody>
              </table>
              {compare.diff && (
                <p className="text-sm" data-testid="scenario-diff">
                  {compare.diff.dias_equivalentes === 0 ? "Sin cambio en la duración." :
                    compare.diff.dias_equivalentes < 0
                      ? <span className="text-emerald-600 font-medium">El escenario termina {Math.abs(compare.diff.dias_equivalentes)} día(s) hábiles antes.</span>
                      : <span className="text-red-600">El escenario termina {compare.diff.dias_equivalentes} día(s) hábiles después.</span>}
                </p>
              )}
            </div>
          ) : scenarios.length > 0 && (
            <div className="rounded-xl border border-dashed border-navy/15 bg-white p-10 text-center text-sm text-navy/40">
              Ejecuta "Simular" en un escenario para ver la comparación contra el plan actual.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
